import { Component } from '@theme/component';

/**
 * @typedef {object} CustomerReviewsRefs
 * @property {HTMLElement} list - The review cards
 * @property {HTMLElement} summaryRating - Stars in the intro card
 * @property {HTMLElement} count - "Based on [count] Reviews"
 * @property {HTMLTemplateElement} cardTemplate - Markup for one review card
 * @property {HTMLElement} [seeAll] - Last card, linking to all reviews
 * @property {HTMLElement} [recommend] - "96% of reviewers recommend us" in the last card
 */

/**
 * @typedef {object} OkendoReview
 * @property {string} [body]
 * @property {string} [title]
 * @property {number} [rating]
 * @property {string} [languageCode]
 * @property {string} [productName]
 * @property {{ displayName?: string, isVerified?: boolean }} [reviewer]
 */

const API = 'https://api.okendo.io/v1/stores/';
const MIN_WORDS = 8;
// Okendo's default titles add nothing ("5 Stars"), so those cards go without one
const GENERIC_TITLE = /^\s*\d\s*stars?\s*$/i;

/**
 * Customer reviews: fills the cards and the review count from Okendo. The row itself is plain CSS
 * overflow with snap; this script only loads data.
 *
 * @extends {Component<CustomerReviewsRefs>}
 */
class CustomerReviewsComponent extends Component {
  requiredRefs = ['list', 'summaryRating', 'count', 'cardTemplate'];

  #loaded = false;

  connectedCallback() {
    super.connectedCallback();
    // Reconnecting (e.g. the editor moving the section) keeps what's already rendered
    if (!this.#loaded) {
      this.#loaded = true;
      this.#loadReviews();
      this.#loadCount();
    }
  }

  get #storeUrl() {
    return `${API}${encodeURIComponent(this.dataset.subscriberId || '')}`;
  }

  get #productIds() {
    return (this.dataset.productIds || '').split(',').filter(Boolean);
  }

  /**
   * @param {string} url
   * @returns {Promise<any>}
   */
  #fetchJson(url) {
    // No custom headers: Okendo's CORS preflight rejects them
    return fetch(url)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  /* ---------- Review count (Okendo aggregate) ---------- */

  async #loadCount() {
    if (!this.dataset.subscriberId) return;
    const ids = this.#productIds;
    const urls = ids.length
      ? ids.map((id) => `${this.#storeUrl}/products/shopify-${id}/review_aggregate`)
      : [`${this.#storeUrl}/review_aggregate`];

    const aggregates = await Promise.all(urls.map((url) => this.#fetchJson(url)));
    let count = 0;
    let total = 0;
    let recommended = 0;
    for (const data of aggregates) {
      const aggregate = data?.reviewAggregate;
      if (!aggregate) continue;
      count += Number(aggregate.reviewCount ?? aggregate.ratingAndReviewCount) || 0;
      total += Number(aggregate.reviewRatingValuesTotal ?? aggregate.ratingAndReviewValuesTotal) || 0;
      recommended += Number(aggregate.recommendationCount) || 0;
    }
    if (!count) return;
    this.#showRecommend(recommended, count);

    const { count: countNode, summaryRating } = this.refs;
    const locale = this.dataset.locale || undefined;
    const average = Math.round((total / count) * 10) / 10;
    countNode.textContent = (this.dataset.countText || '[count]').replace('[count]', count.toLocaleString(locale));
    countNode.hidden = false;
    summaryRating.setAttribute('aria-label', this.#ratingLabel(average));
    this.#fillStars(summaryRating, Math.round(average));
  }

  /**
   * Share of reviewers who recommend the products, in the last card. Stays hidden when Okendo has no figure.
   * @param {number} recommended
   * @param {number} count
   */
  #showRecommend(recommended, count) {
    const { recommend } = this.refs;
    const value = recommend?.querySelector('[data-recommend-value]');
    if (!recommend || !value || !recommended) return;
    value.textContent = `${Math.round((recommended / count) * 100)}%`;
    recommend.hidden = false;
  }

  /* ---------- Review cards ---------- */

  async #loadReviews() {
    const max = Number(this.dataset.limit) || 8;
    if (!this.dataset.subscriberId) return this.#render([]);

    const ids = this.#productIds;
    const urls = ids.length
      ? ids.map((id) => `${this.#storeUrl}/products/shopify-${id}/reviews?limit=25&orderBy=date%20desc`)
      : [`${this.#storeUrl}/reviews?limit=50&orderBy=date%20desc`];

    const minRating = Number(this.dataset.minRating) || 4;
    const language = (this.dataset.locale || 'en').split('-')[0].toLowerCase();
    const pools = await Promise.all(
      urls.map((url) => this.#fetchJson(url).then((data) => this.#qualifying(data?.reviews, minRating, language)))
    );

    this.#render(this.#interleave(pools, max));
  }

  /**
   * Okendo syndicates reviews from every store; keep the storefront's language, good ratings and enough words.
   * @param {OkendoReview[] | undefined} reviews
   * @param {number} minRating
   * @param {string} language
   * @returns {OkendoReview[]}
   */
  #qualifying(reviews, minRating, language) {
    if (!Array.isArray(reviews)) return [];
    return reviews.filter((review) => {
      const body = String(review.body || '').trim();
      const reviewLanguage = String(review.languageCode || '').split('-')[0].toLowerCase();
      return (
        body.split(/\s+/).length >= MIN_WORDS &&
        (Number(review.rating) || 0) >= minRating &&
        (!reviewLanguage || reviewLanguage === language)
      );
    });
  }

  /**
   * Round-robin across products so every chosen product is represented.
   * @param {OkendoReview[][]} pools
   * @param {number} max
   * @returns {OkendoReview[]}
   */
  #interleave(pools, max) {
    const output = [];
    for (let round = 0; output.length < max; round++) {
      let added = false;
      for (const pool of pools) {
        const review = pool[round];
        if (!review || output.length >= max) continue;
        output.push(review);
        added = true;
      }
      if (!added) break;
    }
    return output;
  }

  /** @param {OkendoReview[]} reviews */
  #render(reviews) {
    const { list, cardTemplate, seeAll } = this.refs;
    for (const placeholder of list.querySelectorAll('[data-placeholder]')) placeholder.remove();

    const fragment = document.createDocumentFragment();
    for (const review of reviews) {
      const card = /** @type {DocumentFragment} */ (cardTemplate.content.cloneNode(true));
      const rating = Math.round(Number(review.rating) || 0);
      const title = String(review.title || '').trim();

      this.#setText(card, 'product', review.productName);
      this.#setText(card, 'title', GENERIC_TITLE.test(title) ? '' : title);
      this.#setText(card, 'body', `“${String(review.body || '').trim()}”`);
      this.#setText(card, 'name', review.reviewer?.displayName);

      const ratingNode = /** @type {HTMLElement | null} */ (card.querySelector('[data-field="rating"]'));
      if (ratingNode) {
        ratingNode.setAttribute('aria-label', this.#ratingLabel(rating));
        this.#fillStars(ratingNode, rating);
      }

      const verified = /** @type {HTMLElement | null} */ (card.querySelector('[data-field="verified"]'));
      if (verified) verified.hidden = !review.reviewer?.isVerified;

      fragment.append(card);
    }
    // Reviews go before the "See all reviews" card
    list.insertBefore(fragment, seeAll ?? null);
  }

  /**
   * Text only (never HTML): review content comes from customers.
   * @param {DocumentFragment} card
   * @param {string} field
   * @param {string | undefined} value
   */
  #setText(card, field, value) {
    const node = card.querySelector(`[data-field="${field}"]`);
    if (node) node.textContent = String(value || '').trim();
  }

  /** @param {number} rating */
  #ratingLabel(rating) {
    return (this.dataset.ratingLabel || '[rating] / 5').replace('[rating]', String(rating));
  }

  /**
   * @param {HTMLElement} container
   * @param {number} filled
   */
  #fillStars(container, filled) {
    container.querySelectorAll('[data-star]').forEach((star) => {
      star.classList.toggle('filled-star', Number(/** @type {HTMLElement} */ (star).dataset.star) <= filled);
    });
  }
}

if (!customElements.get('customer-reviews-component')) {
  customElements.define('customer-reviews-component', CustomerReviewsComponent);
}
